import { BrowserProvider } from 'ethers';
import { Contract } from 'ethers';
import { Connection, PublicKey } from '@solana/web3.js';
import Decimal from 'decimal.js';
import priceService from './priceService';
import { supabase } from '../lib/supabase';

class PortfolioService {
  constructor() {
    this.ethereumProvider = null;
    this.solanaConnection = new Connection('https://api.mainnet-beta.solana.com');
    this.portfolioCache = new Map();
  }

  // Save wallet address to database
  async saveWalletAddress(userId, chain, walletAddress) {
    try {
      const { data, error } = await supabase?.from('user_wallets')?.upsert({
          user_id: userId,
          chain,
          wallet_address: walletAddress,
          is_primary: true,
          last_synced_at: new Date()?.toISOString()
        }, {
          onConflict: 'user_id,chain,wallet_address'
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving wallet address:', error);
      throw error;
    }
  }

  // Get user's wallet addresses from database
  async getUserWallets(userId) {
    try {
      const { data, error } = await supabase?.from('user_wallets')?.select('*')?.eq('user_id', userId);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user wallets:', error);
      return [];
    }
  }

  // Save portfolio assets to database
  async savePortfolioAssets(userId, walletId, assets) {
    try {
      const assetsToSave = assets?.map(asset => ({
        user_id: userId,
        wallet_id: walletId,
        symbol: asset?.symbol,
        name: asset?.name,
        balance: asset?.balance,
        decimals: asset?.decimals,
        chain: asset?.chain,
        contract_address: asset?.contractAddress || null,
        icon: asset?.icon,
        color: asset?.color,
        last_price: asset?.price,
        last_updated: new Date()?.toISOString()
      }));

      // Delete existing assets for this wallet and re-insert
      await supabase?.from('portfolio_assets')?.delete()?.eq('wallet_id', walletId);

      const { data, error } = await supabase?.from('portfolio_assets')?.insert(assetsToSave)?.select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving portfolio assets:', error);
      throw error;
    }
  }

  // Get portfolio assets from database
  async getPortfolioAssetsFromDB(userId) {
    try {
      const { data, error } = await supabase?.from('portfolio_assets')?.select('*')?.eq('user_id', userId);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching portfolio assets:', error);
      return [];
    }
  }

  // Multi-chain portfolio aggregation
  async getMultiChainPortfolio(wallets) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Save wallet addresses if provided
      if (wallets?.ethereum) {
        await this.saveWalletAddress(user?.id, 'ethereum', wallets?.ethereum);
      }
      if (wallets?.solana) {
        await this.saveWalletAddress(user?.id, 'solana', wallets?.solana);
      }

      // Fetch live blockchain data
      const portfolios = await Promise.all([
        this.getEthereumPortfolio(wallets?.ethereum),
        this.getSolanaPortfolio(wallets?.solana)
      ]);

      const combinedAssets = this.aggregateAssets(portfolios);
      const totalValue = this.calculateTotalValue(combinedAssets);
      const allocations = this.calculateAllocations(combinedAssets, totalValue);

      // Save to database
      const userWallets = await this.getUserWallets(user?.id);
      for (const wallet of userWallets) {
        const walletAssets = allocations?.filter(a => a?.chain === wallet?.chain);
        if (walletAssets?.length > 0) {
          await this.savePortfolioAssets(user?.id, wallet?.id, walletAssets);
        }
      }

      // Create snapshot
      await this.createPortfolioSnapshot(user?.id, totalValue, allocations);

      return {
        assets: allocations,
        totalValue,
        chains: {
          ethereum: portfolios?.[0],
          solana: portfolios?.[1]
        },
        lastUpdated: new Date()?.toISOString()
      };
    } catch (error) {
      console.error('Error fetching multi-chain portfolio:', error);
      
      // Fallback to database if blockchain fetch fails
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (user) {
          const dbAssets = await this.getPortfolioAssetsFromDB(user?.id);
          const totalValue = this.calculateTotalValue(dbAssets);
          return {
            assets: dbAssets,
            totalValue,
            chains: {},
            error: error?.message
          };
        }
      } catch (dbError) {
        console.error('Error fetching from database:', dbError);
      }

      return {
        assets: [],
        totalValue: new Decimal(0),
        chains: {},
        error: error?.message
      };
    }
  }

  // Create portfolio snapshot
  async createPortfolioSnapshot(userId, totalValue, assets) {
    try {
      const { error } = await supabase?.from('portfolio_snapshots')?.insert({
          user_id: userId,
          total_value: parseFloat(totalValue?.toString()),
          snapshot_data: assets
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error creating portfolio snapshot:', error);
    }
  }

  // Get portfolio history from snapshots
  async getPortfolioHistory(userId, days = 7) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      const { data, error } = await supabase?.from('portfolio_snapshots')?.select('total_value, created_at')?.eq('user_id', userId)?.gte('created_at', startDate?.toISOString())?.order('created_at', { ascending: true });

      if (error) throw error;

      return data?.map(snapshot => ({
        date: snapshot?.created_at,
        value: parseFloat(snapshot?.total_value),
        timestamp: new Date(snapshot.created_at)?.getTime()
      })) || [];
    } catch (error) {
      console.error('Error fetching portfolio history:', error);
      return [];
    }
  }

  // Ethereum portfolio tracking
  async getEthereumPortfolio(address) {
    if (!address) return { assets: [], totalValue: new Decimal(0) };

    try {
      const provider = new BrowserProvider(window?.ethereum);
      const assets = [];

      // Get ETH balance
      const ethBalance = await provider?.getBalance(address);
      const ethAmount = new Decimal(ethBalance.toString())?.div(new Decimal(10)?.pow(18));
      
      const ethPrice = await priceService?.getPrice('ETH');
      const ethValue = ethAmount?.mul(new Decimal(ethPrice?.price || 0));

      assets?.push({
        symbol: 'ETH',
        name: 'Ethereum',
        balance: ethAmount?.toString(),
        decimals: 18,
        price: ethPrice?.price || 0,
        value: ethValue?.toString(),
        change24h: ethPrice?.change24h || 0,
        chain: 'ethereum',
        icon: 'Hexagon',
        color: '#627EEA'
      });

      // Get ERC-20 token balances (common tokens)
      const tokenBalances = await this.getERC20Balances(address, provider);
      assets?.push(...tokenBalances);

      const totalValue = assets?.reduce((sum, asset) => 
        sum?.add(new Decimal(asset?.value || 0)), new Decimal(0)
      );

      return { assets, totalValue };
    } catch (error) {
      console.error('Error fetching Ethereum portfolio:', error);
      return { assets: [], totalValue: new Decimal(0), error: error?.message };
    }
  }

  // Solana portfolio tracking
  async getSolanaPortfolio(address) {
    if (!address) return { assets: [], totalValue: new Decimal(0) };

    try {
      const publicKey = new PublicKey(address);
      const assets = [];

      // Get SOL balance
      const solBalance = await this.solanaConnection?.getBalance(publicKey);
      const solAmount = new Decimal(solBalance)?.div(new Decimal(10)?.pow(9));
      
      const solPrice = await priceService?.getPrice('SOL');
      const solValue = solAmount?.mul(new Decimal(solPrice?.price || 0));

      assets?.push({
        symbol: 'SOL',
        name: 'Solana',
        balance: solAmount?.toString(),
        decimals: 9,
        price: solPrice?.price || 0,
        value: solValue?.toString(),
        change24h: solPrice?.change24h || 0,
        chain: 'solana',
        icon: 'Zap',
        color: '#14F195'
      });

      // Get SPL token balances
      const tokenBalances = await this.getSPLTokenBalances(address);
      assets?.push(...tokenBalances);

      const totalValue = assets?.reduce((sum, asset) => 
        sum?.add(new Decimal(asset?.value || 0)), new Decimal(0)
      );

      return { assets, totalValue };
    } catch (error) {
      console.error('Error fetching Solana portfolio:', error);
      return { assets: [], totalValue: new Decimal(0), error: error?.message };
    }
  }

  // Get ERC-20 token balances for common tokens
  async getERC20Balances(address, provider) {
    const tokens = [
      { symbol: 'USDC', address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', decimals: 6, name: 'USD Coin', icon: 'DollarSign', color: '#2775CA' },
      { symbol: 'USDT', address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', decimals: 6, name: 'Tether', icon: 'DollarSign', color: '#26A17B' },
      { symbol: 'DAI', address: '0x6B175474E89094C44Da98b954EedeAC495271d0F', decimals: 18, name: 'Dai Stablecoin', icon: 'DollarSign', color: '#F5AC37' }
    ];

    const balances = [];

    for (const token of tokens) {
      try {
        const contract = new Contract(
          token.address,
          ['function balanceOf(address) view returns (uint256)'],
          provider
        );

        const balance = await contract?.balanceOf(address);
        const amount = new Decimal(balance.toString())?.div(new Decimal(10)?.pow(token?.decimals));

        if (amount?.greaterThan(0)) {
          const price = await priceService?.getPrice(token?.symbol);
          const value = amount?.mul(new Decimal(price?.price || 0));

          balances?.push({
            symbol: token?.symbol,
            name: token?.name,
            balance: amount?.toString(),
            decimals: token?.decimals,
            price: price?.price || 0,
            value: value?.toString(),
            change24h: price?.change24h || 0,
            chain: 'ethereum',
            icon: token?.icon,
            color: token?.color,
            contractAddress: token?.address
          });
        }
      } catch (error) {
        console.error(`Error fetching ${token?.symbol} balance:`, error);
      }
    }

    return balances;
  }

  // Get SPL token balances
  async getSPLTokenBalances(address) {
    try {
      const publicKey = new PublicKey(address);
      const tokenAccounts = await this.solanaConnection?.getParsedTokenAccountsByOwner(
        publicKey,
        { programId: new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA') }
      );

      const balances = [];

      for (const account of tokenAccounts?.value) {
        const tokenData = account?.account?.data?.parsed?.info;
        const amount = new Decimal(tokenData.tokenAmount.amount)?.div(new Decimal(10)?.pow(tokenData?.tokenAmount?.decimals));

        if (amount?.greaterThan(0)) {
          balances?.push({
            symbol: 'SPL',
            name: 'SPL Token',
            balance: amount?.toString(),
            decimals: tokenData?.tokenAmount?.decimals,
            price: 0,
            value: '0',
            change24h: 0,
            chain: 'solana',
            icon: 'Coins',
            color: '#14F195',
            mint: tokenData?.mint
          });
        }
      }

      return balances;
    } catch (error) {
      console.error('Error fetching SPL tokens:', error);
      return [];
    }
  }

  // Aggregate assets from multiple chains
  aggregateAssets(portfolios) {
    const assetMap = new Map();

    portfolios?.forEach(portfolio => {
      portfolio?.assets?.forEach(asset => {
        const key = `${asset?.symbol}-${asset?.chain}`;
        if (assetMap?.has(key)) {
          const existing = assetMap?.get(key);
          existing.balance = new Decimal(existing.balance)?.add(new Decimal(asset?.balance))?.toString();
          existing.value = new Decimal(existing.value)?.add(new Decimal(asset?.value))?.toString();
        } else {
          assetMap?.set(key, { ...asset });
        }
      });
    });

    return Array.from(assetMap?.values());
  }

  // Calculate total portfolio value
  calculateTotalValue(assets) {
    return assets?.reduce((sum, asset) => 
      sum?.add(new Decimal(asset?.value || asset?.last_price * asset?.balance || 0)), new Decimal(0)
    );
  }

  // Calculate asset allocations
  calculateAllocations(assets, totalValue) {
    if (totalValue?.equals(0)) {
      return assets?.map(asset => ({ ...asset, allocation: 0 }));
    }

    return assets?.map(asset => ({
      ...asset,
      allocation: new Decimal(asset?.value || 0)?.div(totalValue)?.mul(100)?.toNumber()
    }));
  }

  // Portfolio performance analytics
  async getPortfolioPerformance(wallets, timeframe = '24h') {
    try {
      const currentPortfolio = await this.getMultiChainPortfolio(wallets);
      
      // Calculate performance metrics
      const totalValue = parseFloat(currentPortfolio?.totalValue?.toString());
      const profitLoss = this.calculateProfitLoss(currentPortfolio?.assets);
      const roi = this.calculateROI(currentPortfolio?.assets);

      return {
        totalValue,
        profitLoss,
        roi,
        topPerformer: this.getTopPerformer(currentPortfolio?.assets),
        worstPerformer: this.getWorstPerformer(currentPortfolio?.assets),
        diversificationScore: this.calculateDiversification(currentPortfolio?.assets),
        timeframe
      };
    } catch (error) {
      console.error('Error calculating portfolio performance:', error);
      return null;
    }
  }

  // Calculate profit/loss
  calculateProfitLoss(assets) {
    return assets?.reduce((total, asset) => {
      const value = parseFloat(asset?.value || 0);
      const change = (value * (asset?.change24h || 0)) / 100;
      return total + change;
    }, 0);
  }

  // Calculate ROI
  calculateROI(assets) {
    const totalValue = assets?.reduce((sum, asset) => sum + parseFloat(asset?.value || 0), 0);
    const profitLoss = this.calculateProfitLoss(assets);
    return totalValue > 0 ? (profitLoss / totalValue) * 100 : 0;
  }

  // Get top performing asset
  getTopPerformer(assets) {
    return assets?.reduce((top, asset) => 
      (asset?.change24h || 0) > (top?.change24h || 0) ? asset : top
    , assets?.[0] || null);
  }

  // Get worst performing asset
  getWorstPerformer(assets) {
    return assets?.reduce((worst, asset) => 
      (asset?.change24h || 0) < (worst?.change24h || 0) ? asset : worst
    , assets?.[0] || null);
  }

  // Calculate portfolio diversification score (0-100)
  calculateDiversification(assets) {
    if (assets?.length === 0) return 0;
    
    // Calculate Herfindahl-Hirschman Index (HHI)
    const hhi = assets?.reduce((sum, asset) => {
      const allocation = asset?.allocation || 0;
      return sum + Math.pow(allocation, 2);
    }, 0);

    // Convert to diversification score (inverse of concentration)
    const maxHHI = 10000; // Maximum concentration (100^2)
    return Math.max(0, 100 - (hhi / maxHHI) * 100);
  }
}

export const portfolioService = new PortfolioService();
export default portfolioService;